import { Building2, CreditCard, Receipt } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

/**
 * Platform financials.
 *
 * This page deliberately shows no platform-wide financial aggregate. The previous
 * version summed only the first 50 invoice rows returned by a truncated client page
 * and presented the result as if it represented the whole platform (see ROADMAP
 * PA-0.3). Those figures were misleading, so they have been removed.
 *
 * Real reporting is split into two distinct concerns that must not be conflated:
 *   1. Platform subscription billing (TPV SaaS) — what The Original charges each
 *      organisation. Reserved for PA-5.1.
 *   2. Tenant customer invoices — invoices each organisation issues to its own
 *      customers. Investigated per-tenant via the detail workflow, never as a
 *      single cross-tenant total.
 */
const PlatformFinancials = () => {
  return (
    <div className="space-y-6 p-4 md:p-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="max-w-2xl">
          <h2 className="font-display text-3xl text-foreground">Financials</h2>
          <p className="mt-1 text-muted-foreground">
            Platform financials are intentionally not shown as a single total. A trustworthy figure
            requires aggregating every invoice server-side, not summing the first page a browser
            happened to load.
          </p>
        </div>
        <Badge variant="outline">No aggregate shown</Badge>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-start gap-3 space-y-0">
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <CreditCard className="h-5 w-5" />
          </div>
          <div className="space-y-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle>Platform subscription billing (TPV SaaS)</CardTitle>
              <Badge variant="secondary">Coming in PA-5.1</Badge>
            </div>
            <CardDescription>
              The subscription fees The Original charges each organisation to use the platform.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <p>
            This is the platform's own revenue: recurring SaaS charges billed to tenants. It is a
            separate ledger from the money tenants collect from their customers and must never be
            added to it.
          </p>
          <p>
            A verified subscription-revenue view is reserved for <span className="font-medium text-foreground">PA-5.1</span>,
            which will source figures from the billing system rather than a truncated client page.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-start gap-3 space-y-0">
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Receipt className="h-5 w-5" />
          </div>
          <div className="space-y-1.5">
            <CardTitle>Tenant customer invoices</CardTitle>
            <CardDescription>
              Invoices each organisation issues to its own customers for bookings and services.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <p>
            These invoices belong to individual tenants, not to the platform. There is no legitimate
            platform-wide total to present here, and cross-tenant sums are both misleading and a
            privacy concern.
          </p>
          <p className="flex items-start gap-2">
            <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-foreground" />
            <span>
              To support a specific invoice, open the relevant <span className="font-medium text-foreground">tenant,
              customer or booking detail</span> workflow, where the invoice is shown in the context of the
              organisation that owns it.
            </span>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default PlatformFinancials;
